import {GROUP_PALETTE} from "../Statistics/format.js";

/**
 * Formatierung für die Nutzungsauswertung - ergänzt die Basisformate aus
 * Components/Statistics/format.js (Zahlen, Datum) um alles, was hier
 * zusätzlich gebraucht wird: Zeitdauern, Zeitstempel in Europe/Berlin und
 * die feste Bereichsliste aus der Spezifikation (Abschnitt 5). Gespeichert
 * wird überall in UTC, ANGEZEIGT wird ausschließlich Europe/Berlin - sonst
 * zeigt eine Arbeitszeitauswertung im Sommer zwei Stunden zu früh.
 */

const dateTimeFormatter = new Intl.DateTimeFormat("de-DE", {
    timeZone: "Europe/Berlin",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
});

const timeFormatter = new Intl.DateTimeFormat("de-DE", {
    timeZone: "Europe/Berlin",
    hour: "2-digit",
    minute: "2-digit",
});

/**
 * Zeitstempel kommen von den Endpunkten immer als ISO 8601 MIT Offset
 * (siehe Spezifikation Abschnitt 1) - new Date(iso) reicht deshalb, die
 * Umrechnung nach Europe/Berlin übernimmt der Formatter.
 */
export function formatDateTimeDE(iso) {
    if (!iso) {
        return "–";
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return "–";
    }
    return dateTimeFormatter.format(date);
}

export function formatTimeDE(iso) {
    if (!iso) {
        return "–";
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return "–";
    }
    return timeFormatter.format(date);
}

/**
 * Sekunden als lesbare Stundenanzeige, z. B. 24060 -> "6:41 Std.". Minuten
 * werden abgeschnitten statt gerundet, damit Tageswerte in einer Summe
 * nicht durch Rundung von der Gesamtsumme abweichen.
 */
export function formatDuration(seconds) {
    if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) {
        return "–";
    }
    const total = Math.max(0, Math.floor(Number(seconds)));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    return `${hours}:${String(minutes).padStart(2, "0")} Std.`;
}

export const WEEKDAY_LABELS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
export const WEEKDAY_LABELS_LONG = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];
export const HOURS = Array.from({length: 24}, (_, i) => i);

/**
 * Bereichs-Klarnamen aus der Spezifikation (Abschnitt 5) - feste Liste,
 * weil es dafür bewusst keinen eigenen Options-Endpunkt gibt (die
 * Zuordnung steht serverseitig in config/activity.php).
 */
export const BEREICHE = [
    {value: "bestellungen", label: "Bestellungen"},
    {value: "lager", label: "Lager"},
    {value: "verkaufsstatistik", label: "Verkaufsstatistik"},
    {value: "verwaltung", label: "Verwaltung"},
    {value: "kampagnen", label: "Kampagnen"},
    {value: "support", label: "Support"},
    {value: "roadmap", label: "Roadmap"},
    {value: "import", label: "Import"},
    {value: "artikel", label: "Artikel"},
    {value: "einstellungen", label: "Einstellungen"},
    {value: "nutzung", label: "Nutzung"},
    {value: "sonstiges", label: "Sonstiges"},
];

export function bereichLabel(value) {
    return BEREICHE.find((eintrag) => eintrag.value === value)?.label || value || "Sonstige";
}

/**
 * Feste Farbe je Bereich - anders als monochromeScale() (Rang-basierte
 * Abstufung EINER Farbe, siehe NutzungBereichsverteilung) braucht eine
 * gestapelte Verteilung über mehrere Mitarbeiter hinweg eine Farbe, die für
 * denselben Bereich in JEDER Zeile und auf der ganzen Seite gleich bleibt -
 * sonst wäre "Support" in Zeile 1 grün und in Zeile 2 orange. Zyklisch aus
 * GROUP_PALETTE plus Ergänzungsfarben, weil es mehr Bereiche (12) als
 * Palettenfarben (7) gibt.
 */
const BEREICH_FARBSKALA = [...GROUP_PALETTE, "#495057", "#74c69d", "#f08c00", "#5c7cfa", "#c2255c"];

const BEREICH_FARBEN = Object.fromEntries(
    BEREICHE.map((eintrag, index) => [eintrag.value, BEREICH_FARBSKALA[index % BEREICH_FARBSKALA.length]])
);

export function bereichFarbe(value) {
    return BEREICH_FARBEN[value] || "#adb5bd";
}

/**
 * Deckfarbe einer Heatmap-Kachel - einfarbige Abstufung (Primärfarbe der
 * Statistik-Palette, siehe GROUP_PALETTE[0] in Statistics/format.js) statt
 * einer Regenbogenskala. Wird hier als RGB-Tripel dupliziert, weil
 * Statistics/format.js keine hexToRgb-Hilfsfunktion exportiert und diese
 * Datei nicht verändert werden soll.
 */
export function heatmapColor(value, max) {
    if (!value || !max) {
        return "rgba(47, 111, 208, 0.06)";
    }
    const anteil = Math.min(1, value / max);
    const alpha = 0.1 + anteil * 0.8;
    return `rgba(47, 111, 208, ${alpha.toFixed(3)})`;
}
